import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  AdminListRequest,
  CheckEmailRequest,
  CheckPhoneNumberRequest,
  InsertAdminRequest,
} from './model';

import type { Admin } from './model/admin';

import { ApiResponse, ListResponse } from '../../../model';

import { API_CONSTANTS } from '../../../utils/constants';
import { baseQueryWithReauth } from '../../../apis/baseQuery';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  tagTypes: ['AdminIdentity', 'AdminList'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getAdminIdentity: builder.query<ApiResponse<Admin>, void>({
      query: () => ({
        url: API_CONSTANTS.ADMIN.ADMIN_IDENTITY_URL,
        method: 'GET',
      }),
      providesTags: ['AdminIdentity'],
    }),
    getAdminList: builder.query<ListResponse<Admin>, AdminListRequest>({
      query: (body) => ({
        url: API_CONSTANTS.ADMIN.ADMIN_LIST_URL,
        method: 'POST',
        body,
      }),
      providesTags: ['AdminList'],
    }),
    deleteAdmin: builder.mutation<ApiResponse<unknown>, number>({
      query: (adminId) => ({
        url: API_CONSTANTS.ADMIN.DELETE_ADMIN_URL(adminId),
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminList'],
    }),
    insertAdmin: builder.mutation<ApiResponse<unknown>, InsertAdminRequest>({
      query: (newAdmin) => ({
        url: API_CONSTANTS.ADMIN.INSERT_ADMIN_URL,
        method: 'POST',
        body: newAdmin,
      }),
      invalidatesTags: ['AdminList'],
    }),
    getAdminById: builder.query<ApiResponse<Admin>, number>({
      query: (adminId) => ({
        url: API_CONSTANTS.ADMIN.DETAILS_ADMIN_URL(adminId),
        method: 'GET',
      }),
    }),
    updateAdmin: builder.mutation<
      ApiResponse<unknown>,
      { id: number } & InsertAdminRequest
    >({
      query: ({ id, ...body }) => ({
        url: API_CONSTANTS.ADMIN.ADMIN_UPDATE_URL(id),
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AdminList'],
    }),
    checkAdminEmail: builder.mutation<ApiResponse<boolean>, CheckEmailRequest>({
      query: (body) => ({
        url: API_CONSTANTS.ADMIN.CHECK_EMAIL_URL,
        method: 'POST',
        body,
      }),
    }),
    checkAdminPhoneNumber: builder.mutation<
      ApiResponse<boolean>,
      CheckPhoneNumberRequest
    >({
      query: (body) => ({
        url: API_CONSTANTS.ADMIN.CHECK_PHONE_NUMBER_URL,
        method: 'POST',
        body,
      }),
    }),
    switchRole: builder.mutation<
      ApiResponse<{
        access_token: string;
        activeGroupId: number;
        groupIds: number[];
      }>,
      { targetGroupId: number }
    >({
      query: (body) => ({
        url: API_CONSTANTS.ADMIN.ADMIN_SWITCH_ROLE_URL,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetAdminIdentityQuery,
  useLazyGetAdminIdentityQuery,
  useGetAdminListQuery,
  useDeleteAdminMutation,
  useInsertAdminMutation,
  useLazyGetAdminByIdQuery,
  useUpdateAdminMutation,
  useCheckAdminEmailMutation,
  useCheckAdminPhoneNumberMutation,
  useSwitchRoleMutation,
} = adminApi;

export default adminApi;
